import { APIActions } from "../services/restAPI/ListsV2";

// PDF file signature bytes
const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46, 0x2d]; // "%PDF-"
const PDF_SIGNATURE_SEARCH_LIMIT = 1024;

export interface PocResponse {
   type: string;
   data: unknown;
   url?: string;
}

export interface PopUpData {
   message: string;
   buttons?: { label: string };
}

export interface PocResponseCallbacks {
   onOpenForm: (data: unknown) => void;
   onOpenUrl: (url: string) => void;
   onRefreshList: () => void;
   onPopup: (data: PopUpData) => void;
   onOpenWorkflow: (instanceId: string, rowId: string) => void;
}

export interface PocResponseContext {
   rowId?: string;
   callbacks: PocResponseCallbacks;
}

const isPdfBytes = (buf: ArrayBuffer): boolean => {
   const bytes = new Uint8Array(buf);
   const limit = Math.min(PDF_SIGNATURE_SEARCH_LIMIT, bytes.length);

   for (let i = 0; i + PDF_SIGNATURE.length <= limit; i++) {
      if (PDF_SIGNATURE.every((byte, index) => bytes[i + index] === byte)) {
         return true;
      }
   }
   return false;
};

const downloadBlob = (blob: Blob, filename = "document.pdf"): void => {
   const url = URL.createObjectURL(blob);
   const a = document.createElement("a");
   a.href = url;
   a.download = filename;
   a.style.display = "none";

   document.body.appendChild(a);
   a.click();

   // Clean up immediately
   document.body.removeChild(a);
   URL.revokeObjectURL(url);
};

export const decodeJson = (buf: ArrayBuffer): unknown => {
   const text = new TextDecoder("utf-8").decode(new Uint8Array(buf));
   return JSON.parse(text);
};

const extractFilenameFromContentDisposition = (headerValue?: string) => {
   if (!headerValue) return undefined;

   const parts = headerValue.split(";").map((header) => header.trim());
   const params = new Map<string, string>();

   for (const part of parts.slice(1)) {
      const equalIndex = part.indexOf("=");
      if (equalIndex === -1) continue;
      const key = part.slice(0, equalIndex).trim().toLowerCase();
      const raw = part.slice(equalIndex + 1).trim();
      const value = raw.replace(/^"|"$/g, "");
      params.set(key, value);
   }

   const filenameStar = params.get("filename*");
   if (filenameStar) {
      const encoded = filenameStar.replace(/^(utf-8''|UTF-8'')/, "");
      try {
         return decodeURIComponent(encoded);
      } catch {
         return encoded;
      }
   }

   return params.get("filename");
};

export const handlePocResponse = async (
   url: string,
   context: PocResponseContext,
): Promise<boolean> => {
   try {
      const result = await APIActions(url);
      const buf: ArrayBuffer = result.data;
      // Handle PDF downloads directly
      if (isPdfBytes(buf)) {
         const blob = new Blob([buf], { type: "application/pdf" });
         const contentDisposition = result.headers?.["content-disposition"];
         const filename =
            extractFilenameFromContentDisposition(contentDisposition) ?? "document.pdf";
         downloadBlob(blob, filename);
         return true; // PDF downloaded successfully
      }

      // Parse JSON response
      const responseData = decodeJson(buf) as PocResponse;
      if (!responseData?.type || !responseData?.data) {
         return false;
      }

      const { type, data, url: responseUrl } = responseData;
      const { callbacks, rowId } = context;

      switch (type) {
         case "openForm":
            callbacks.onOpenForm(data);
            break;
         case "openUrl":
            if (responseUrl) {
               callbacks.onOpenUrl(responseUrl);
            }
            break;
         case "refreshList":
            callbacks.onRefreshList();
            break;
         case "openWorkflow": {
            const instanceId = (responseData as any)?.data?.start?.instanceId;
            if (instanceId && rowId) {
               callbacks.onOpenWorkflow(instanceId, rowId);
            }
            break;
         }
         case "popUp": {
            callbacks.onPopup(data as PopUpData);
            break;
         }
         default:
            console.warn(`Unknown response type: ${type}`);
            return false;
      }

      return true;
   } catch (error) {
      console.error("Failed to execute POC action:", error);
      return false;
   }
};
